<template>
  <v-card>
    <v-card-title class="primary white--text headline">
      {{ $t('dueDiligence') }}<template v-if="!$vuetify.breakpoint.smAndDown"> — {{ queueItem.tractName }}</template>
      <v-spacer/>
      <BaseDialogActions hideRefresh hideFullscreen customCancel @close="$emit('close')"/>
    </v-card-title>

    <v-card-text class="pt-4">
      <v-row no-gutters>
      <v-col cols="12" lg="7" class="pr-lg-4">
      <v-alert v-if="editing" type="warning" text outlined dense icon="mdi-pencil" class="mb-4 dd-banner">
        {{ $t('editingSubmissionBanner') }}
      </v-alert>

      <v-alert v-if="showOwnSubmissionNotice" type="info" text outlined dense icon="mdi-account-lock-outline" class="mb-4 dd-banner">
        {{ $t('ownSubmissionNotice') }}
      </v-alert>

      <v-alert v-if="isRejected" type="info" text outlined dense icon="mdi-history" class="mb-4 dd-banner">
        {{ $t('decidedDriftCaveat') }}
      </v-alert>

      <DueDiligenceTractDetails class="mb-4" :queueItem="queueItem"/>

      <TractExemptionPanel
      v-if="showExemptionPanel"
      class="mb-4"
      historicalView
      :exemption="exemption"
      :latestApproval="queueItem"
      :readOnly="exemptionReadOnly"
      :saving="savingExemption"
      @save="saveExemption"/>

      <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px 16px;">
        <span class="d-inline-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-clipboard-search-outline</v-icon>
          <span class="text-h6 font-weight-regular">{{ $t('mitigationEntries') }}</span>
        </span>
        <span v-if="locationAndAcres" class="text-caption grey--text text--darken-1">
          <v-icon small class="mr-1">mdi-map-marker-outline</v-icon>{{ locationAndAcres }}
        </span>
        <span v-if="queueItem.requestedAt" class="text-caption grey--text text--darken-1">
          <v-icon small class="mr-1">mdi-calendar-clock</v-icon>{{ $t('submitted') }} {{ submittedDate }}
        </span>
        <v-menu v-if="foresterName && hasContactDetails" offset-y :close-on-content-click="false" v-model="contactOpen">
          <template #activator="{ on }">
            <v-chip small outlined v-on="on">
              <v-icon small left>mdi-account-hard-hat-outline</v-icon>
              {{ foresterName }}
              <v-icon small right>{{ contactOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </v-chip>
          </template>
          <v-list dense>
            <v-list-item v-if="foresterEmail" :href="`mailto:${foresterEmail}`">
              <v-icon small class="mr-2">mdi-email-outline</v-icon>{{ foresterEmail }}
            </v-list-item>
            <v-list-item v-if="foresterPhone">
              <v-icon small class="mr-2">mdi-phone-outline</v-icon>{{ foresterPhone }}
            </v-list-item>
          </v-list>
        </v-menu>
        <v-chip v-else-if="foresterName" small outlined>
          <v-icon small left>mdi-account-hard-hat-outline</v-icon>
          {{ foresterName }}
        </v-chip>
      </div>

      <p v-if="exemption" class="text-caption grey--text text--darken-1 mt-n2 mb-3">
        <v-icon x-small class="mr-1">mdi-information-outline</v-icon>{{ $t('exemptEntriesInformationalInfo') }}
      </p>

      <v-sheet v-if="displayItems.length === 0" outlined rounded class="pa-6 text-center">
        <v-icon size="32" color="grey">mdi-clipboard-outline</v-icon>
        <p class="text-body-2 grey--text text--darken-1 mb-0 mt-2">{{ $t('noMitigationEntries') }}</p>
      </v-sheet>

      <div v-else class="d-flex flex-column" style="gap: 12px;">
        <v-card
        v-for="(item, index) in displayItems"
        :key="item.tractMitigationItemId"
        outlined
        :class="{ 'ddv-entry-editing': editing }"
        :style="editing && item.color ? { borderLeftColor: item.color } : null">
          <div class="d-flex align-center px-4 py-3 flex-wrap" style="gap: 8px;">
            <v-icon :color="item.color || 'primary'" class="mr-1">mdi-square-rounded</v-icon>
            <span class="text-subtitle-2">{{ item.mitigationGroupName }}</span>
            <v-scale-transition origin="center">
              <v-chip v-if="editing && editedFlags[index]" x-small label class="dd-count-pill">{{ $t('edited') }}</v-chip>
            </v-scale-transition>
            <v-spacer/>
            <span class="dd-eyebrow">{{ presenceLabel(item) }}</span>
            <v-btn-toggle
            v-if="editing"
            v-model="editableItems[index].riskPresence"
            mandatory
            dense
            rounded>
              <v-btn
              v-for="option in presenceToggleOptions"
              :key="String(option.value)"
              :value="option.value"
              small
              :color="editableItems[index].riskPresence === option.value ? option.color : undefined"
              :class="editableItems[index].riskPresence === option.value ? 'white--text' : 'grey--text text--darken-1'">
                <v-icon small left>{{ option.icon }}</v-icon>{{ $t(option.label) }}
              </v-btn>
            </v-btn-toggle>
            <PresenceChip v-else :present="isPresent(item)"/>
          </div>

          <v-divider/>

          <div class="px-4 py-3">
            <template v-if="speciesFor(item).length > 0">
              <div class="dd-eyebrow mb-1">{{ $t('speciesInThisGroup') }}</div>
              <v-sheet outlined rounded>
                <div
                v-for="(sp, spIndex) in speciesFor(item)"
                :key="spIndex"
                class="d-flex align-center justify-space-between px-3 py-2"
                :class="{ 'dd-species-divider': spIndex > 0 }">
                  <span>
                    <span class="text-body-2 font-weight-medium">{{ sp.commonName }}</span>
                    <span v-if="sp.scientificName" class="text-caption font-italic grey--text text--darken-1 ml-2">{{ sp.scientificName }}</span>
                  </span>
                  <template v-if="canCheckNatureServe(sp)">
                    <v-btn v-if="$vuetify.breakpoint.lgAndUp" small outlined color="primary" class="ml-2 flex-shrink-0" @click="$emit('check-nature-serve', { common: sp.commonName, scientific: sp.scientificName })">
                      <v-icon small left>mdi-database-search-outline</v-icon>
                      {{ $t('checkNatureServe') }}
                    </v-btn>
                    <v-tooltip v-else bottom color="primary">
                      <template #activator="{ on }">
                        <v-btn v-on="on" small icon outlined color="primary" class="ml-2 flex-shrink-0" @click="$emit('check-nature-serve', { common: sp.commonName, scientific: sp.scientificName })">
                          <v-icon small>mdi-database-search-outline</v-icon>
                        </v-btn>
                      </template>
                      <span class="subtitle-1 white--text">{{ $t('checkNatureServe') }}</span>
                    </v-tooltip>
                  </template>
                </div>
              </v-sheet>
            </template>
            <div v-else class="text-caption font-italic grey--text text--darken-1">{{ $t('noRecordedSpecies') }}</div>

            <HabitatAutocomplete
            v-if="editing && item.requiresHabitat"
            v-model="editableItems[index].mitigationHabitatIds"
            :habitats="allMitigationHabitats"
            class="mt-3"/>
            <div v-else-if="habitatNamesFor(item).length > 0" class="mt-3 d-flex flex-wrap" style="gap: 7px;">
              <v-chip v-for="(name, hIndex) in habitatNamesFor(item)" :key="hIndex" small outlined>
                <v-icon small left color="primary">mdi-pine-tree</v-icon>{{ name }}
              </v-chip>
            </div>

            <template v-if="editing">
              <v-expand-transition>
                <v-textarea
                v-if="isPresent(editableItems[index])"
                v-model="editableItems[index].mitigationComment"
                :label="$t('riskMitigationComments')"
                :placeholder="$t('riskMitigationCommentsPlaceholder')"
                :hint="$t('riskMitigationCommentsHelp')"
                persistent-hint
                :rules="[commentRequiredRule]"
                :counter="maxCommentLength"
                :maxlength="maxCommentLength"
                rows="2"
                auto-grow
                outlined
                dense
                class="mt-3"/>
              </v-expand-transition>
              <v-expand-transition>
                <div
                v-if="!isPresent(editableItems[index]) && (items[index].mitigationComment || '').length > 0"
                class="mt-3 text-caption grey--text text--darken-1 d-flex align-center">
                  <v-icon small color="warning" class="mr-1">mdi-information-outline</v-icon>
                  {{ $t('commentClearedOnSave') }}
                </div>
              </v-expand-transition>
            </template>
            <MitigationCommentPreview v-else-if="item.mitigationComment" :comment="item.mitigationComment" class="mt-3"/>
          </div>
        </v-card>
      </div>

      <!-- Reviewer comment — required on a Return, optional on an Approve -->
      <v-alert v-if="isDecided && queueItem.comment" :type="isApproved ? 'success' : 'error'" text outlined dense icon="mdi-comment-quote-outline" class="mt-4 mb-0 dd-banner">
        <div class="dd-eyebrow mb-1">{{ $t('reviewerComment') }}</div>
        {{ queueItem.comment }}
      </v-alert>

      <div v-if="isDecided" class="text-caption grey--text text--darken-1 mt-4 d-flex align-center flex-wrap" style="gap: 4px 16px;">
        <span v-if="queueItem.resolvedByUserName" class="d-inline-flex align-center">
          <v-icon small class="mr-1">mdi-account-check-outline</v-icon>{{ $t('reviewed') }} {{ $t('by') }} {{ queueItem.resolvedByUserName }}
        </span>
        <span v-if="resolvedDate" class="d-inline-flex align-center">
          <v-icon small class="mr-1">mdi-calendar-check</v-icon>{{ resolvedDate }}
        </span>
      </div>
      </v-col>
      <v-col cols="12" lg="5" class="ddv-map-col mt-6 mt-lg-0">
        <DueDiligenceMapPanel
        :key="queueItem.tractId"
        :tractId="queueItem.tractId"
        :latitude="queueItem.latitude"
        :longitude="queueItem.longitude"/>
      </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions class="px-4 pb-4">
      <template v-if="isDecided">
        <v-btn text outlined color="black" @click="$emit('close')">{{ $t('close') }}</v-btn>
        <v-spacer/>
        <v-chip v-if="isApproved" color="success" text-color="white" label>
          <v-icon small left>mdi-check-decagram</v-icon>{{ $t('approved') }}
        </v-chip>
        <v-chip v-else color="error" text-color="white" label>
          <v-icon small left>mdi-close-octagon</v-icon>{{ $t('rejected') }}
        </v-chip>
      </template>

      <template v-else-if="editing">
        <span class="text-caption font-italic grey--text text--darken-1">
          {{ $t('editingSubmission') }}<span v-if="changedCount > 0"> · {{ $t('nEdited', { n: changedCount }) }}</span>
        </span>
        <v-spacer/>
        <v-btn text outlined color="black" @click="cancelEdit">{{ $t('cancel') }}</v-btn>
        <v-btn color="success" class="tertiary--text" :disabled="!canSaveChanges || changedCount === 0" :loading="saving" @click="saveChanges">
          <v-icon left>mdi-content-save</v-icon>{{ $t('saveChanges') }}
        </v-btn>
      </template>

      <template v-else>
        <v-btn
        v-if="canDecide && displayItems.length > 0"
        text
        color="grey darken-1"
        @click="enterEdit">
          <v-icon small left>mdi-pencil</v-icon>{{ $vuetify.breakpoint.mdAndDown ? $t('edit') : $t('editSubmission') }}
        </v-btn>
        <v-spacer/>
        <template v-if="canDecide">
          <v-btn color="error" outlined @click="confirmingReject = true">
            <v-icon left>mdi-close-octagon-outline</v-icon>{{ $t('reject') }}
          </v-btn>
          <v-btn color="success" class="tertiary--text" @click="confirmingApprove = true">
            <v-icon left>mdi-check-decagram</v-icon>{{ $t('approve') }}
          </v-btn>
        </template>
      </template>
    </v-card-actions>

    <v-dialog v-model="confirmingApprove" width="500px">
      <ConfirmDialog
      v-if="confirmingApprove"
      :title="$t('approveDueDiligenceTitle')"
      :body="$t('approveDueDiligenceBody', { name: queueItem.tractName })"
      :confirmText="$t('approve')"
      color="success"
      :width="$vuetify.breakpoint.mdAndDown ? '100%' : '500px'"
      @confirm="performApprove"
      @cancel="cancelApprove">
        <template #custom-body>
          <v-textarea
          v-model="approveComment"
          :label="$t('approveCommentLabel')"
          :hint="$t('approveCommentHint')"
          persistent-hint
          :counter="maxCommentLength"
          :maxlength="maxCommentLength"
          rows="2"
          auto-grow
          outlined
          dense
          class="mt-4"/>
        </template>
      </ConfirmDialog>
    </v-dialog>

    <v-dialog v-model="confirmingReject" width="500px">
      <v-card>
        <v-card-title class="white--text headline error" style="word-break: break-word">
          {{ $t('rejectDueDiligenceTitle') }}
        </v-card-title>
        <v-card-text class="mt-6">
          <p class="subtitle-1 black--text font-weight-medium">{{ $t('rejectDueDiligenceBody', { name: queueItem.tractName }) }}</p>
          <v-textarea
          v-model="rejectComment"
          :label="$t('rejectionCommentsForForester')"
          :counter="maxCommentLength"
          :maxlength="maxCommentLength"
          rows="3"
          auto-grow
          outlined/>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-btn text outlined color="black" @click="cancelReject">{{ $t('cancel') }}</v-btn>
          <v-spacer/>
          <v-btn color="error" :disabled="!rejectCommentValid" :loading="deciding" @click="performReject">
            {{ $t('reject') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { DueDiligenceApprovalStatus, RiskPresence, UserClaims } from '@/utils/Enumerations.js'
import { StringConstants } from '@/utils/constants'
import { userAssignedClaim } from '@/utils/ClaimUtility'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { isApproved, isRejected, isDecided, locationAndAcres, canCheckNatureServe, presenceLabel } from '@/utils/dueDiligence/presentation.js'
import { compareGroupRowPrecedence } from '@/utils/dueDiligence/groupPrecedence.js'
import { dueDiligenceApprovalEnabled } from '@/utils/dueDiligence/mode.js'

export default {
  name: 'ViewDueDiligenceDialog',

  components: {
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    MitigationCommentPreview: () => import('@/components/due-diligence/MitigationCommentPreview.vue'),
    DueDiligenceMapPanel: () => import('@/components/due-diligence/DueDiligenceMapPanel.vue'),
    HabitatAutocomplete: () => import('@/components/due-diligence/HabitatAutocomplete.vue'),
    PresenceChip: () => import('@/components/due-diligence/PresenceChip.vue'),
    TractExemptionPanel: () => import('@/components/due-diligence/TractExemptionPanel.vue'),
    DueDiligenceTractDetails: () => import('@/components/due-diligence/DueDiligenceTractDetails.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue')
  },

  props: {
    queueItem: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    editing: false,
    editableItems: [],
    saving: false,
    deciding: false,
    confirmingApprove: false,
    confirmingReject: false,
    rejectComment: '',
    approveComment: '',
    contactOpen: false,
    savingExemption: false,
    maxCommentLength: StringConstants.ExtraExtraExtraLongStringLength
  }),

  watch: {
    queueItem: {
      immediate: true,
      handler () {
        if (this.isDecided) {
          this.cancelEdit()
          this.confirmingApprove = false
          this.confirmingReject = false
        }
      }
    }
  },

  computed: {
    ...mapGetters('mitigation-habitat', ['allMitigationHabitats']),
    ...mapGetters('user', ['userInfo']),

    // Sorted here rather than at render — editableItems is index-matched against this list
    items () {
      return [...(this.queueItem.entries ?? [])].sort(compareGroupRowPrecedence)
    },

    exemption () {
      return this.queueItem.dueDiligenceExemption ?? null
    },

    showExemptionPanel () {
      return !!this.exemption || (this.canDecide && !this.isDecided)
    },

    exemptionReadOnly () {
      return this.isDecided || this.editing || !this.canDecide
    },

    displayItems () {
      return this.editing ? this.editableItems : this.items
    },

    isApproved () {
      return isApproved(this.queueItem)
    },

    isRejected () {
      return isRejected(this.queueItem)
    },

    isDecided () {
      return isDecided(this.queueItem)
    },

    presenceToggleOptions () {
      return [
        { value: RiskPresence.Present.value, color: 'warning', icon: 'mdi-alert', label: 'present' },
        { value: RiskPresence.NotPresent.value, color: 'success', icon: 'mdi-check', label: 'notPresent' }
      ]
    },

    isSustainabilityAdmin () {
      return userAssignedClaim(UserClaims.SustainabilityAdmin)
    },

    canReview () {
      return this.isSustainabilityAdmin && dueDiligenceApprovalEnabled()
    },

    isOwnSubmission () {
      return this.queueItem.requestedByUserId === this.userInfo.applicationUserId
    },

    canDecide () {
      return this.canReview && !this.isOwnSubmission
    },

    showOwnSubmissionNotice () {
      return this.canReview && this.isOwnSubmission && !this.isDecided
    },

    locationAndAcres () {
      return locationAndAcres(this.queueItem)
    },

    foresterName () {
      return this.queueItem.requestedByUserName
    },

    foresterEmail () {
      return this.queueItem.requestedByUserEmail
    },

    foresterPhone () {
      return this.queueItem.requestedByUserPhone
    },

    hasContactDetails () {
      return !!(this.foresterEmail || this.foresterPhone)
    },

    submittedDate () {
      return this.queueItem.requestedAt ? utcToLocalDate(this.queueItem.requestedAt) : ''
    },

    resolvedDate () {
      return this.queueItem.resolvedAt ? utcToLocalDate(this.queueItem.resolvedAt) : ''
    },

    canSaveChanges () {
      return this.editableItems.every(item => {
        if (!this.isPresent(item)) return true
        if ((item.mitigationComment ?? '').trim().length === 0) return false
        if (item.requiresHabitat && (item.mitigationHabitatIds ?? []).length === 0) return false
        return true
      })
    },

    rejectCommentValid () {
      return this.rejectComment.trim().length > 0
    },

    editedFlags () {
      if (!this.editing) return []
      return this.editableItems.map((edited, index) => this.entryEdited(edited, this.items[index]))
    },

    changedCount () {
      return this.editedFlags.filter(Boolean).length
    }
  },

  methods: {
    ...mapActions('due-diligence-approval', ['decideDueDiligence']),
    ...mapActions('tract', ['updateTractMitigationItem', 'setTractDueDiligenceExemption']),
    ...mapActions('mitigation-habitat', ['fetchMitigationHabitats']),

    presenceLabel (item) {
      return presenceLabel(item.requiresHabitat)
    },

    canCheckNatureServe,

    isPresent (item) {
      return item?.riskPresence === RiskPresence.Present.value
    },

    // The toggle only offers Present / Not Present, so an unaddressed entry reads as Not Present on
    // both sides of the comparison and is not counted as an edit until the reviewer touches it.
    editablePresence (riskPresence) {
      return riskPresence === RiskPresence.Present.value ? RiskPresence.Present.value : RiskPresence.NotPresent.value
    },

    speciesFor (item) {
      return item.speciesHits ?? []
    },

    habitatsFor (item) {
      return [...(item.habitats ?? [])].sort((a, b) => a.name.localeCompare(b.name))
    },

    habitatNamesFor (item) {
      if (item.habitats?.length) return this.habitatsFor(item).map(h => h.name)
      return [...(item.habitatNames ?? [])].sort((a, b) => a.localeCompare(b))
    },

    entryEdited (edited, original) {
      if (!edited || !original) return false
      if (edited.riskPresence !== this.editablePresence(original.riskPresence)) return true
      const originalHabitatIds = (original.habitats ?? []).map(h => h.mitigationHabitatId).sort((a, b) => a - b)
      const editedHabitatIds = [...(edited.mitigationHabitatIds ?? [])].sort((a, b) => a - b)
      if (originalHabitatIds.length !== editedHabitatIds.length) return true
      if (originalHabitatIds.some((id, index) => id !== editedHabitatIds[index])) return true
      return this.isPresent(edited) &&
        (edited.mitigationComment ?? '') !== (original.mitigationComment ?? '')
    },

    commentRequiredRule (value) {
      return (value ?? '').trim().length > 0 || this.$t('commentRequired')
    },

    async enterEdit () {
      if (this.isDecided) return
      if (this.allMitigationHabitats.length === 0) await this.fetchMitigationHabitats()
      this.editableItems = this.items.map(item => ({
        ...item,
        riskPresence: this.editablePresence(item.riskPresence),
        mitigationComment: item.mitigationComment ?? '',
        mitigationHabitatIds: this.habitatsFor(item).map(h => h.mitigationHabitatId)
      }))
      this.editing = true
    },

    cancelEdit () {
      this.editing = false
      this.editableItems = []
    },

    async saveChanges () {
      if (this.isDecided) return
      this.saving = true
      try {
        const changed = this.editableItems.filter((item, index) => this.editedFlags[index])
        for (const item of changed) {
          await this.updateTractMitigationItem({
            tractId: this.queueItem.tractId,
            mitigationItem: {
              tractMitigationItemId: item.tractMitigationItemId,
              mitigationGroupId: item.mitigationGroupId,
              riskPresence: item.riskPresence,
              mitigationHabitatIds: item.mitigationHabitatIds ?? [],
              mitigationComment: this.isPresent(item) ? item.mitigationComment : ''
            }
          })
        }
        this.setSnack(this.$t('submissionUpdated'))
        this.editing = false
        this.editableItems = []
        this.$emit('submission-updated')
      } finally {
        this.saving = false
      }
    },

    async saveExemption ({ dueDiligenceExemptionId }) {
      if (this.isDecided || !this.canDecide) return
      this.savingExemption = true
      try {
        await this.setTractDueDiligenceExemption({ tractId: this.queueItem.tractId, dueDiligenceExemptionId, modifiedAt: this.queueItem.tractModifiedAt })
        this.setSnack(this.$t(dueDiligenceExemptionId == null ? 'exemptionCleared' : 'exemptionUpdated'))
        this.$emit('submission-updated')
      } finally {
        this.savingExemption = false
      }
    },

    cancelApprove () {
      this.confirmingApprove = false
      this.approveComment = ''
    },

    async performApprove () {
      if (this.isDecided || !this.canDecide) return
      this.deciding = true
      try {
        await this.decideDueDiligence({
          tractId: this.queueItem.tractId,
          decision: DueDiligenceApprovalStatus.Approved.value,
          comment: this.approveComment.trim()
        })
        this.setSnack(this.$t('markedAsApproved', { name: this.queueItem.tractName }))
        this.confirmingApprove = false
        this.approveComment = ''
        this.$emit('decided')
      } finally {
        this.deciding = false
      }
    },

    cancelReject () {
      this.confirmingReject = false
      this.rejectComment = ''
    },

    async performReject () {
      if (this.isDecided || !this.canDecide) return
      this.deciding = true
      try {
        await this.decideDueDiligence({
          tractId: this.queueItem.tractId,
          decision: DueDiligenceApprovalStatus.Returned.value,
          comment: this.rejectComment.trim()
        })
        this.setSnack(this.$t('markedAsRejected', { name: this.queueItem.tractName }))
        this.confirmingReject = false
        this.rejectComment = ''
        this.$emit('rejected')
      } finally {
        this.deciding = false
      }
    }
  }
}
</script>

<style scoped>
.ddv-entry-editing {
  background: rgba(var(--color-primary-rgb), 0.03);
  border-left-width: 3px !important;
  transition: background-color 0.25s ease;
}
.ddv-map-col {
  position: sticky;
  top: 0;
  align-self: flex-start;
}
@media (prefers-reduced-motion: reduce) {
  .ddv-entry-editing {
    transition: none;
  }
}
</style>
